<template>
    <div class="w-full">
        <div class="text-center mb-4 p-8">
            <h1 class="text-center b-2 text-2xl lg:text-4xl font-bold">
                Position Sizing Calculator
            </h1>

            <p class="text-gray-600">
                Manage trading risk with adjusting position size of trades
            </p>
        </div>

        <div class="block lg:flex p-4">
            <div class="w-full max-w-4xl p-4 lg:shadow rounded">
                <form>
                    <div class="md:flex md:items-center mb-6">
                        <div class="md:w-1/3">
                            <label class="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                                   for="total-capital">
                                total capital
                            </label>
                        </div>

                        <div class="md:w-2/3">
                            <input class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                                   id="total-capital" v-model.number="capital" type="number">
                        </div>
                    </div>

                    <div class="md:flex md:items-center mb-6">
                        <div class="md:w-1/3">
                            <label class="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                                   for="risk-percentage">
                                risk percentage
                            </label>
                        </div>

                        <div class="md:w-2/3">
                            <input class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                                   id="risk-percentage" v-model.number="riskPercentage" type="number">
                        </div>
                    </div>

                    <div class="md:flex md:items-center mb-6">
                        <div class="md:w-1/3">
                            <label class="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                                   for="entry-price">
                                entry price
                            </label>
                        </div>

                        <div class="md:w-2/3">
                            <input class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                                   id="entry-price" v-model.number="entryPrice" type="number">
                        </div>
                    </div>

                    <div class="md:flex md:items-center mb-6">
                        <div class="md:w-1/3">
                            <label class="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                                   for="stop-price">
                                stop-loss price
                            </label>
                        </div>

                        <div class="md:w-2/3">
                            <input class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                                   id="stop-price" v-model.number="stopLoss" type="number">
                        </div>
                    </div>

                    <div class="md:flex md:items-center mb-6">
                        <div class="md:w-1/3">
                            <label class="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                                   for="take-profit-price">
                                take-profit price (optional)
                            </label>
                        </div>

                        <div class="md:w-2/3">
                            <input class="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
                                   id="take-profit-price" v-model.number="takeProfit" type="number">
                        </div>
                    </div>
                </form>
            </div>

            <div class="w-12"></div>

            <div class="w-full max-w-4xl p-4 lg:shadow rounded">
                <div class="text-2xl font-semibold text-center uppercase">result</div>

                <div class="mt-4" v-if="isValid">
                    <table class="table-fixed w-full">
                        <tbody>
                        <tr class="bg-gray-100">
                            <td class="border w-1/2 px-4 py-2">quantity of shares</td>
                            <td class="border w-1/2 px-4 py-2" v-text="quantityOfShares"></td>
                        </tr>
                        <tr>
                            <td class="border w-1/2 px-4 py-2">trade type</td>
                            <td class="border w-1/2 px-4 py-2" v-text="tradeType"></td>
                        </tr>
                        <tr class="bg-gray-100">
                            <td class="border px-4 py-2">position value</td>
                            <td class="border px-4 py-2" v-text="positionValue"></td>
                        </tr>
                        <tr>
                            <td class="border px-4 py-2">risk/share</td>
                            <td class="border px-4 py-2" v-text="riskPerShare"></td>
                        </tr>
                        <tr class="bg-gray-100">
                            <td class="border px-4 py-2">risk/position</td>
                            <td class="border px-4 py-2" v-text="riskPerPosition"></td>
                        </tr>

                        <tr v-if="takeProfit">
                            <td class="border px-4 py-2">reward/share</td>
                            <td class="border px-4 py-2" v-text="rewardPerShare"></td>
                        </tr>
                        <tr v-if="takeProfit" class="bg-gray-100">
                            <td class="border px-4 py-2">reward/position</td>
                            <td class="border px-4 py-2" v-text="rewardPerPosition"></td>
                        </tr>
                        <tr v-if="takeProfit">
                            <td class="border px-4 py-2">risk/reward ratio (R)</td>
                            <td class="border px-4 py-2" v-text="riskRewardRatio"></td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex flex-col content-center justify-center h-full" v-else="isValid">
                    <div class="text-gray-500 text-center">
                        Please fill all the required fields
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                capital: null,
                riskPercentage: null,
                entryPrice: null,
                stopLoss: null,
                takeProfit: null
            };
        },

        computed: {
            riskPerShare() {
                return Math.abs(this.entryPrice - this.stopLoss)
            },

            isValid() {
                return this.capital && this.riskPercentage && this.entryPrice && this.stopLoss
            },

            quantityOfShares() {
                return this.riskPerPosition / this.riskPerShare
            },

            positionValue() {
                return this.quantityOfShares * this.entryPrice
            },

            riskPerPosition() {
                return (this.riskPercentage / 100) * this.capital
            },

            rewardPerShare() {
                return Math.abs(this.entryPrice - this.takeProfit)
            },

            rewardPerPosition() {
                return this.rewardPerShare * this.quantityOfShares
            },

            riskRewardRatio() {
                return this.rewardPerShare / this.riskPerShare
            },

            tradeType() {
                if (this.entryPrice > this.stopLoss) {
                    return 'long'
                }

                if (this.entryPrice < this.stopLoss) {
                    return 'short'
                }

                return ''
            }
        },
    }
</script>
